import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DateTimeField } from "@/components/ui/datetime-field";
import { allowedGroupings } from "./hooks";
import {
  GROUP_BY_LABELS,
  PRESET_LABELS,
  type ReportGroupBy,
  type ReportPreset,
} from "@/types/production-report.types";

// Spec 083 (T032) — período e agrupamento.
//
// ⚠️ O agrupamento por DIA some da lista acima do teto, em vez de aparecer e ser recusado pela
// rota. Oferecer o que será negado é pior que não oferecer: o gestor clica, espera, e recebe um
// erro que ele não causou.

interface Props {
  preset: ReportPreset;
  onPresetChange: (preset: ReportPreset) => void;
  from: string;
  to: string;
  onRangeChange: (range: { from: string; to: string }) => void;
  groupBy: ReportGroupBy;
  onGroupByChange: (groupBy: ReportGroupBy) => void;
}

const PRESETS: ReportPreset[] = ["7d", "30d", "thisMonth", "lastMonth", "thisYear", "custom"];

/**
 * Data civil sobre o componente canônico de data/hora.
 *
 * O relatório trabalha em datas, e o componente canônico fala ISO com hora. Converter aqui é o
 * preço de NÃO usar `<input type="date">` cru, que o design system proíbe — e que traria máscara,
 * calendário e comportamento de teclado diferentes do resto do produto.
 */
function DateOnlyField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <DateTimeField
      label={label}
      value={`${value}T00:00:00.000Z`}
      onChange={(iso) => {
        if (iso) onChange(iso.slice(0, 10));
      }}
    />
  );
}

export function PeriodPicker({
  preset,
  onPresetChange,
  from,
  to,
  onRangeChange,
  groupBy,
  onGroupByChange,
}: Props) {
  const grupos = allowedGroupings(from, to);

  return (
    <div className="flex flex-wrap items-end gap-4">
      <Tabs value={preset} onValueChange={(v) => onPresetChange(v as ReportPreset)}>
        <TabsList variant="pill" aria-label="Período do relatório">
          {PRESETS.map((p) => (
            <TabsTrigger key={p} value={p}>
              {PRESET_LABELS[p]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {preset === "custom" ? (
        <div className="flex flex-wrap items-end gap-3">
          <DateOnlyField label="De" value={from} onChange={(v) => onRangeChange({ from: v, to })} />
          <DateOnlyField label="Até" value={to} onChange={(v) => onRangeChange({ from, to: v })} />
        </div>
      ) : null}

      <Tabs value={groupBy} onValueChange={(v) => onGroupByChange(v as ReportGroupBy)}>
        <TabsList variant="pill" aria-label="Agrupar por">
          {grupos.map((g) => (
            <TabsTrigger key={g} value={g}>
              {GROUP_BY_LABELS[g]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}
